import { useState, useEffect } from "react";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";
const SESSION_KEY = "passcode_authenticated";
const REMEMBER_KEY = "passcode_remember";
const REMEMBER_EXPIRES_KEY = "passcode_remember_expires";

interface PasscodeGateProps {
  children: React.ReactNode;
}

export const PasscodeGate = ({ children }: PasscodeGateProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passcode, setPasscode] = useState("");
  const [error, setError] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPasscode, setShowPasscode] = useState(false);

  useEffect(() => {
    // Only check if remembered authentication is still valid
    const remembered = localStorage.getItem(REMEMBER_KEY);
    const expires = localStorage.getItem(REMEMBER_EXPIRES_KEY);

    if (remembered === "true" && expires) {
      const expirationTime = parseInt(expires, 10);
      if (Date.now() < expirationTime) {
        // Still valid, auto-authenticate
        setIsAuthenticated(true);
      } else {
        // Expired, clean up
        localStorage.removeItem(REMEMBER_KEY);
        localStorage.removeItem(REMEMBER_EXPIRES_KEY);
      }
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      // Validate passcode with backend
      const response = await fetch(`${API_URL}/api/validate-passcode`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ passcode }),
      });

      const { valid } = await response.json();

      if (valid) {
        // If remember me is checked, store in localStorage with 30-day expiration
        if (rememberMe) {
          const expirationTime = Date.now() + 30 * 24 * 60 * 60 * 1000; // 30 days
          localStorage.setItem(REMEMBER_KEY, "true");
          localStorage.setItem(REMEMBER_EXPIRES_KEY, expirationTime.toString());
        } else {
          // Ensure we don't have stale remember me data
          localStorage.removeItem(REMEMBER_KEY);
          localStorage.removeItem(REMEMBER_EXPIRES_KEY);
        }

        setIsAuthenticated(true);
        setError("");
      } else {
        setError("Invalid passcode");
        setPasscode("");
        setIsLoading(false);
      }
    } catch (err) {
      setError("Failed to connect to server. Please try again.");
      setPasscode("");
      setIsLoading(false);
    }
  };

  if (isAuthenticated) {
    return <>{children}</>;
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#2a2a2a",
      }}
    >
      <div
        style={{
          background: "#1a1a1a",
          padding: "2.5rem",
          borderRadius: "8px",
          border: "1px solid #404040",
          minWidth: "350px",
        }}
      >
        <h1
          style={{
            margin: "0 0 2rem 0",
            fontSize: "1.5rem",
            color: "#ffffff",
            fontWeight: 400,
            textAlign: "center",
          }}
        >
          Enter Passcode
        </h1>
        <form onSubmit={handleSubmit}>
          <div
            style={{
              position: "relative",
              marginBottom: error ? "1rem" : "1.5rem",
            }}
          >
            <input
              type={showPasscode ? "text" : "password"}
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              placeholder="Passcode"
              autoFocus
              disabled={isLoading}
              style={{
                width: "100%",
                padding: "0.875rem 2.5rem 0.875rem 0.875rem",
                fontSize: "1rem",
                border: "1px solid #404040",
                borderRadius: "4px",
                outline: "none",
                boxSizing: "border-box",
                background: "#2a2a2a",
                color: "#ffffff",
                opacity: isLoading ? 0.6 : 1,
                cursor: isLoading ? "not-allowed" : "text",
              }}
            />
            <button
              type="button"
              onClick={() => setShowPasscode(!showPasscode)}
              disabled={isLoading}
              style={{
                position: "absolute",
                right: "0.75rem",
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: isLoading ? "not-allowed" : "pointer",
                color: "#999",
                fontSize: "1.1rem",
                padding: "0.25rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: isLoading ? 0.6 : 1,
                transition: "color 0.2s",
              }}
              onMouseEnter={(e) => {
                if (!isLoading) e.currentTarget.style.color = "#ffffff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "#999";
              }}
              title={showPasscode ? "Hide passcode" : "Show passcode"}
            >
              {showPasscode ? "👁️" : "👁️‍🗨️"}
            </button>
          </div>
          {error && (
            <div
              style={{
                color: "#ff6b6b",
                background: "rgba(255, 107, 107, 0.1)",
                padding: "0.75rem",
                borderRadius: "4px",
                marginBottom: "1rem",
                fontSize: "0.9rem",
                border: "1px solid rgba(255, 107, 107, 0.3)",
              }}
            >
              {error}
            </div>
          )}
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              marginBottom: "1.5rem",
              cursor: isLoading ? "not-allowed" : "pointer",
              color: "#b8b8b8",
              fontSize: "0.9rem",
              opacity: isLoading ? 0.6 : 1,
            }}
          >
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              disabled={isLoading}
              style={{
                cursor: isLoading ? "not-allowed" : "pointer",
                width: "16px",
                height: "16px",
              }}
            />
            Remember me for 30 days
          </label>
          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: "100%",
              padding: "0.875rem",
              fontSize: "1rem",
              fontWeight: 500,
              color: "#ffffff",
              background: isLoading ? "#2a2a2a" : "#404040",
              border: "1px solid #505050",
              borderRadius: "4px",
              cursor: isLoading ? "not-allowed" : "pointer",
              transition: "background 0.2s",
              opacity: isLoading ? 0.6 : 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.currentTarget.style.background = "#505050";
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                e.currentTarget.style.background = "#404040";
              }
            }}
          >
            {isLoading ? (
              <>
                <span
                  style={{
                    display: "inline-block",
                    width: "14px",
                    height: "14px",
                    border: "2px solid #808080",
                    borderTop: "2px solid #ffffff",
                    borderRadius: "50%",
                    animation: "spin 0.8s linear infinite",
                  }}
                />
                Authenticating...
              </>
            ) : (
              "Login"
            )}
          </button>
        </form>
      </div>
      <style>{`
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};
